import React, { ChangeEvent, FC, KeyboardEvent, useCallback, useEffect, useMemo, useState } from "react";
import classnames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { useDebounce, useLoader } from "@shared/hooks";
import { AvatarImage, getInitials } from "@shared/components";
import { User } from "@shared/models";
import { RecipientOption } from "@containers/Recognition/interfaces";

// Relative, not the "@containers"/"@shared" alias -- same jest path-alias resolution constraint
// RecognitionForm.tsx's own top comment documents (no `jest` key in package.json, so an alias only
// resolves when a test explicitly virtual-mocks it). This file's own test imports the REAL formHelpers
// module the same way RecognitionForm.test.tsx does.
import { handlers } from "./formHelpers";

import "./index.scss";

interface RecipientSearchPickerProps {
  // Controlled by RecognitionForm, not owned locally -- lifted so the parent can gate the empty-state
  // suggestion chips (frame 9199:1160) off the SAME text this component searches on: the chips are the
  // empty state and must disappear the instant the person starts typing (frame 9267:1668, "01b ·
  // Searching for a teammate"). Cleared by the parent on both a selection and a `clearRecipient`, never
  // by this component -- one source of truth for "is the To field currently searching."
  query: string;
  onQueryChange: (query: string) => void;
  onSelect: (option: RecipientOption) => void;
  // The signed-in user's own id (string, matching Option.value) -- never a valid recognition recipient.
  // Same list AsyncSelectPickerInput's own `disabledOptionsValues` filtered out.
  excludedUserIds: string[];
}

/**
 * 923-core-context context/engineering/spark-rewards/phase-54-give-recognition-drawer.md: the "To" field's search box and results list (frame 9267:1739,
 * "search results"), replacing rsuite's stock `SelectPicker` menu the old dropdown form rendered via
 * `AsyncSelectPickerInput`. Keeps the SAME data path -- `handlers.user.getData`/`selectData`/
 * `loadingAction`, this feature's own formHelpers.tsx, unchanged -- this is a presentation replacement,
 * not a new data path; only the rendered menu changes, from a flat name list to a purpose-built row
 * (avatar / name / second line).
 *
 * A controlled combobox: DOM focus never leaves the text input. Arrow keys move `highlightedIndex`
 * (local state -- purely which row is "active", not which is chosen), Enter chooses whichever row it
 * currently points at, Escape clears the query (which closes the list, since visibility is driven by
 * `query` being non-empty) -- the same three keys rsuite's own `SelectPicker` menu supported, so
 * replacing its markup doesn't drop keyboard support.
 *
 * The results are sorted alphabetically by full name, mirroring `handlers.user.onSort`'s identical
 * `label.localeCompare` rule the old rsuite picker sorted by -- load-bearing here specifically because
 * the local DB carries several same-named duplicates (pavel: nine separate "Zoriana Hruden" rows), so
 * scanning them together, with the second line doing the disambiguation, only works if they're adjacent.
 *
 * @param props.query - the current search text; empty hides the results list entirely.
 * @param props.onQueryChange - called on every keystroke and on Escape (with "").
 * @param props.onSelect - called with the chosen user's RecipientOption ({ label, value, avatarUrl })
 *   shape -- the exact shape `RecognitionForm`'s own `selectRecipient` and the suggestion chips
 *   already build.
 * @param props.excludedUserIds - user ids left out of the results (the signed-in user).
 */
const RecipientSearchPicker: FC<RecipientSearchPickerProps> = (props) => {
  const { query, onQueryChange, onSelect, excludedUserIds } = props;
  const dispatch = useDispatch();
  const debouncedQuery = useDebounce(query, 500);
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  const users: User[] = useSelector(handlers.user.selectData());

  const { isLoading } = useLoader({
    name: "user",
    actionTypes: useMemo(() => [handlers.user.loadingAction], []),
  });

  useEffect(() => {
    dispatch(handlers.user.getData(debouncedQuery));
  }, [dispatch, debouncedQuery]);

  const isSearching = query.trim().length > 0;

  const results = useMemo(() => {
    if (!isSearching) {
      return [];
    }
    return users
      .filter((user) => !excludedUserIds.includes(String(user.id)))
      .sort((a, b) => `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`));
  }, [isSearching, users, excludedUserIds]);

  // A fresh query invalidates whatever was highlighted under the PREVIOUS one -- otherwise a stale index
  // could point past the end of a shorter results array, or at an unrelated row of a longer one.
  useEffect(() => {
    setHighlightedIndex(0);
  }, [query]);

  const activeIndex = results.length ? Math.min(highlightedIndex, results.length - 1) : -1;

  const choose = useCallback(
    (user: User) => {
      onSelect(handlers.user.prepareOptionFunction(user));
    },
    [onSelect],
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (!isSearching) {
        return;
      }
      // Escape closes regardless of whether there's anything TO navigate -- gating it behind
      // `results.length` (the way ArrowUp/Down/Enter below correctly are) would strand a person on a
      // zero-result search with no keyboard way out, only a mouse click elsewhere.
      if (event.key === "Escape") {
        event.preventDefault();
        onQueryChange("");
        return;
      }
      if (results.length === 0) {
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setHighlightedIndex((index) => (index + 1) % results.length);
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setHighlightedIndex((index) => (index - 1 + results.length) % results.length);
      } else if (event.key === "Enter") {
        event.preventDefault();
        const user = results[activeIndex];
        if (user) {
          choose(user);
        }
      }
    },
    [isSearching, results, activeIndex, choose, onQueryChange],
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      onQueryChange(event.target.value);
    },
    [onQueryChange],
  );

  return (
    <div className="recognition-drawer-recipient-search-container">
      <div className="recognition-drawer-recipient-search">
        <span className="recognition-drawer-recipient-search-glyph" aria-hidden="true">
          ⌕
        </span>
        <input
          type="text"
          name="user"
          className="recognition-drawer-recipient-input"
          placeholder="Search teammates"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          data-testid="recognition-drawer-recipient-search-input"
        />
      </div>
      {isSearching ? (
        <div
          className="recognition-drawer-recipient-results"
          role="listbox"
          data-testid="recognition-drawer-recipient-results"
        >
          {results.length === 0 && isLoading ? (
            <p className="recognition-drawer-recipient-results-loading">Loading...</p>
          ) : (
            results.map((user, index) => {
              const active = index === activeIndex;
              return (
                <button
                  type="button"
                  key={user.id}
                  role="option"
                  aria-selected={active}
                  className={classnames("recognition-drawer-recipient-result", {
                    "recognition-drawer-recipient-result--active": active,
                  })}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => choose(user)}
                  data-testid={`recognition-drawer-recipient-result-${user.id}`}
                >
                  <span className="recognition-drawer-recipient-result-avatar">
                    <AvatarImage imageUrl={user.avatar_url} fallback={getInitials(user.first_name, user.last_name)} />
                  </span>
                  <span className="recognition-drawer-recipient-result-copy">
                    <span className="recognition-drawer-recipient-result-name">
                      {`${user.first_name} ${user.last_name}`}
                    </span>
                    {/* PROVISIONAL, pending pavel's confirmation -- the frame's second line ("Research ·
                        Hudson" / "Infrastructure" / "Delivery") hasn't been confirmed against a named
                        field. Wired to `job_title` (User.tsx) because it's the one field that plausibly
                        covers all three examples; not yet verified as the designer's actual intent.
                        Change ONLY this expression if pavel names a different field. Never renders an
                        empty line -- no `job_title` means the name renders alone. */}
                    {user.job_title ? (
                      <span className="recognition-drawer-recipient-result-subtitle">{user.job_title}</span>
                    ) : null}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
};

export default RecipientSearchPicker;
